<script module>
  import AppLayout from '../../layouts/AppLayout.svelte'
  export const layout = AppLayout
</script>

<script>
  import { Button } from '$lib/components/ui/button/index.js'
  import { Progress } from '$lib/components/ui/progress/index.js'
  import PageContainer from '$lib/components/page-container.svelte'
  import DownloadIcon from '@lucide/svelte/icons/download'

  let { property, rooms, gross_income_cents, deductions, total_deductions_cents, statutory_income_cents } = $props()

  const CURRENT_YEAR = new Date().getFullYear()

  function formatRM(cents) {
    return (cents / 100).toLocaleString('en-MY', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
  }

  function formatRMNeg(cents) {
    return cents === 0 ? '—' : `(${formatRM(cents)})`
  }

  const fullAddress = $derived(
    [property.street_address, property.city, property.state, property.postcode]
      .filter(Boolean)
      .join(', ')
  )
</script>

<svelte:head>
  <title>Review & Summary — MyRentalTax</title>
</svelte:head>

<PageContainer>
  {#snippet breadcrumb()}
    <span class="text-[11px] font-medium text-muted-foreground">Review & Summary · Step 5 of 5</span>
  {/snippet}
    <!-- Header -->
    <div class="flex items-start justify-between gap-3">
      <div class="flex flex-col gap-1">
        <h1 class="text-xl sm:text-2xl font-semibold text-foreground m-0">Review & Summary</h1>
        <p class="text-sm text-muted-foreground m-0 leading-relaxed">Review your entries below. PDF download is coming soon.</p>
      </div>
      <Button variant="outline" size="sm" class="flex-shrink-0 gap-1.5" disabled>
        <DownloadIcon class="w-4 h-4" />
        <span class="hidden sm:inline">Download PDF</span>
        <span class="sm:hidden">PDF</span>
      </Button>
    </div>

    <!-- Progress row (desktop) -->
    <div class="hidden sm:flex items-center gap-3">
      <span class="text-xs font-medium text-muted-foreground">Step 5 of 5</span>
      <Progress value={5} max={5} class="flex-1" />
    </div>

    <!-- Document card -->
    <div class="bg-white border border-zinc-200 rounded-xl p-5 sm:p-8 flex flex-col gap-0 shadow-sm">

      <!-- Document header -->
      <div class="flex items-start justify-between pb-4 border-b border-zinc-100">
        <!-- Logo -->
        <div class="flex items-center gap-2">
          <div class="w-7 h-7 bg-zinc-900 rounded-md flex items-center justify-center text-white flex-shrink-0">
            <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <line x1="3" y1="22" x2="21" y2="22"/><line x1="6" y1="18" x2="6" y2="11"/>
              <line x1="10" y1="18" x2="10" y2="11"/><line x1="14" y1="18" x2="14" y2="11"/>
              <line x1="18" y1="18" x2="18" y2="11"/><polygon points="12 2 20 7 4 7"/>
            </svg>
          </div>
          <span class="text-sm font-semibold text-zinc-900">MyRentalTax</span>
        </div>
        <span class="text-xs font-medium text-zinc-500">Year of Assessment: {CURRENT_YEAR}</span>
      </div>

      <!-- Property info -->
      <div class="py-3 border-b border-zinc-100">
        <p class="text-[11px] font-semibold text-zinc-400 tracking-wider uppercase mb-1">Property</p>
        <p class="text-sm font-medium text-zinc-900">{property.name}</p>
        {#if fullAddress}
          <p class="text-xs text-zinc-500 mt-0.5">{fullAddress}</p>
        {/if}
      </div>

      <!-- INCOME SUMMARY -->
      <div class="py-4 border-b border-zinc-100">
        <p class="text-[10px] font-bold text-zinc-400 tracking-widest uppercase mb-3">Income Summary</p>

        {#if rooms.length === 0}
          <div class="flex justify-between text-sm text-zinc-400 italic py-1">
            <span>No rooms entered</span>
            <span>—</span>
          </div>
        {:else}
          {#each rooms as room}
            <div class="flex justify-between text-sm py-1">
              <span class="text-zinc-600">{room.name}</span>
              <span class="text-zinc-900">RM {formatRM(room.annual_income_cents)}</span>
            </div>
          {/each}
        {/if}

        <!-- Gross total -->
        <div class="flex justify-between text-sm font-semibold mt-2 pt-2 border-t border-zinc-100">
          <span class="text-zinc-900">Gross Rental Income</span>
          <span class="text-zinc-900">RM {formatRM(gross_income_cents)}</span>
        </div>
      </div>

      <!-- ALLOWABLE DEDUCTIONS -->
      <div class="py-4 border-b border-zinc-100">
        <p class="text-[10px] font-bold text-zinc-400 tracking-widest uppercase mb-3">Allowable Deductions</p>

        <div class="flex flex-col gap-1">
          <div class="flex justify-between text-sm py-0.5">
            <span class="text-zinc-600">Loan Interest (YA)</span>
            <span class="text-red-600">{formatRMNeg(deductions.loan_interest_cents)}</span>
          </div>
          <div class="flex justify-between text-sm py-0.5">
            <span class="text-zinc-600">Cukai Taksiran (Assessment Tax)</span>
            <span class="text-red-600">{formatRMNeg(deductions.assessment_tax_cents)}</span>
          </div>
          <div class="flex justify-between text-sm py-0.5">
            <span class="text-zinc-600">Cukai Tanah (Quit Rent)</span>
            <span class="text-red-600">{formatRMNeg(deductions.quit_rent_tax_cents)}</span>
          </div>
          <div class="flex justify-between text-sm py-0.5">
            <span class="text-zinc-600">Servis Charges + Sinking Fund</span>
            <span class="text-red-600">{formatRMNeg(deductions.service_sinking_cents)}</span>
          </div>
          <div class="flex justify-between text-sm py-0.5">
            <span class="text-zinc-600">Utilities (Elektrik + Air + Pembetungan)</span>
            <span class="text-red-600">{formatRMNeg(deductions.utilities_cents)}</span>
          </div>
          {#if deductions.custom_expenses_cents > 0}
            <div class="flex justify-between text-sm py-0.5">
              <span class="text-zinc-600">Custom Expenses</span>
              <span class="text-red-600">{formatRMNeg(deductions.custom_expenses_cents)}</span>
            </div>
          {/if}
        </div>

        <!-- Total deductions -->
        <div class="flex justify-between text-sm font-semibold mt-2 pt-2 border-t border-zinc-100">
          <span class="text-zinc-900">Total Allowable Deductions</span>
          <span class="text-red-600">({formatRM(total_deductions_cents)})</span>
        </div>
      </div>

      <!-- STATUTORY INCOME banner -->
      <div class="mt-4 bg-zinc-900 rounded-xl px-5 py-5 flex flex-col gap-1.5">
        <p class="text-[10px] font-bold text-zinc-400 tracking-widest uppercase m-0">Statutory Income from Rents</p>
        <p class="text-xs text-zinc-500 m-0">After all allowable deductions</p>
        <p class="text-3xl sm:text-4xl font-bold text-white m-0 mt-1">RM {formatRM(statutory_income_cents)}</p>
      </div>

    </div>

  {#snippet footer()}
    <Button variant="outline" href="/properties/{property.id}/utilities">Back</Button>
    <div class="flex-1"></div>
    <Button href="/properties/new">+ Add Another Property</Button>
  {/snippet}
</PageContainer>
